// orderMgmtDb.js
// One-shot checkout API wrapper: backend does payment (if credit) + update checkout info + send-to-merchant + empty cart
//
// Updated to support paymentMethod="cash" and to REMOVE recaptchaToken requirement.

import UserDb from "./userDb";
const axios = require("axios");

const ORDER_MGMT_ROUTE_URL = "/api/orderMgmts";

export default class OrderMgmtDb {
  /*
    placeOrder(payload)

    ✅ Changes:
    - recaptchaToken is no longer required (removed hard check)
    - For creditcard: backend may still require recaptchaToken depending on your server logic
    - For cash: no authorizePayment/cardInfo needed

    Payload example (cash):
    {
      merchantId: String,

      checkoutInfo: { paymentMethod:"cash", total, ... },
      sendOrder: { userInfo:{} },
      emptyCart: { run:true }
    }

    Payload example (creditcard):
    {
      merchantId: String,
      recaptchaToken?: String,     // optional here; backend may require it for card
      authorizePayment: { amount?, cardInfo:{...} },
      checkoutInfo: { paymentMethod:"creditcard", total, ... },
      sendOrder: { userInfo:{} },
      emptyCart: { run:true }
    }
  */

  static async placeOrder(payload) {
    try {
      if (!payload) {
        console.error("Invalid payload(orderMgmt.placeOrder)");
        return null;
      }

      const { merchantId, checkoutInfo, authorizePayment, recaptchaToken } =
        payload;

      if (!merchantId) {
        console.error("Invalid merchantId(orderMgmt.placeOrder)");
        return null;
      }

      const paymentMethod =
        (checkoutInfo && checkoutInfo.paymentMethod) || null;

      if (!paymentMethod) {
        console.error(
          "Missing checkoutInfo.paymentMethod(orderMgmt.placeOrder)"
        );
        return null;
      }

      const isCash = paymentMethod === "cash";
      const isCreditCard = paymentMethod === "creditcard";

      if (!isCash && !isCreditCard) {
        console.error(
          `Invalid paymentMethod:[${paymentMethod}] (orderMgmt.placeOrder)`
        );
        return null;
      }

      // ✅ For creditcard, ensure cardInfo exists client-side (server will also validate)
      if (isCreditCard) {
        if (!authorizePayment || !authorizePayment.cardInfo) {
          console.error(
            "Missing authorizePayment.cardInfo(orderMgmt.placeOrder)"
          );
          return null;
        }

        // ✅ recaptchaToken check removed (because you asked to remove it)
        // If your backend still requires it for credit card, you can warn but not block:
        if (!recaptchaToken) {
          console.warn(
            "Warning: missing recaptchaToken(orderMgmt.placeOrder) - backend may reject creditcard payment"
          );
        }
      }

      // ✅ get uid from cookie (same as ShoppingCartDb)
      const user = await UserDb.getCookie();
      if (!user) {
        console.error("Fail to fetch user cookies(orderMgmt.placeOrder)");
        return null;
      }

      const response =
        (await axios.post(
          `${ORDER_MGMT_ROUTE_URL}/merchantId/${merchantId}/placeOrder/${user.uid}`,
          payload
        )) || {};

      return response.data;
    } catch (ex) {
      console.error(ex);
      return null;
    }
  }

  // (unchanged) Optional: get order status
  static async getOrderStatus(merchantId, orderId) {
    try {
      if (!merchantId) {
        console.error("Invalid merchantId(orderMgmt.getOrderStatus)");
        return null;
      }
      if (!orderId) {
        console.error("Invalid orderId(orderMgmt.getOrderStatus)");
        return null;
      }

      const response =
        (await axios.get(
          `${ORDER_MGMT_ROUTE_URL}/merchantId/${merchantId}/orderId/${orderId}/status`
        )) || {};

      return response.data;
    } catch (ex) {
      console.error(ex);
      return null;
    }
  }

  // (unchanged) Optional: manual resend
  static async resendOrder(merchantId, orderId) {
    try {
      if (!merchantId) {
        console.error("Invalid merchantId(orderMgmt.resendOrder)");
        return null;
      }
      if (!orderId) {
        console.error("Invalid orderId(orderMgmt.resendOrder)");
        return null;
      }

      const response =
        (await axios.post(
          `${ORDER_MGMT_ROUTE_URL}/merchantId/${merchantId}/orderId/${orderId}/resend`
        )) || {};

      return response.data;
    } catch (ex) {
      console.error(ex);
      return null;
    }
  }

  // (unchanged) Optional: cancel/refund/void
  static async voidOrRefund(merchantId, orderId) {
    try {
      if (!merchantId) {
        console.error("Invalid merchantId(orderMgmt.voidOrRefund)");
        return null;
      }
      if (!orderId) {
        console.error("Invalid orderId(orderMgmt.voidOrRefund)");
        return null;
      }

      const response =
        (await axios.post(
          `${ORDER_MGMT_ROUTE_URL}/merchantId/${merchantId}/orderId/${orderId}/voidOrRefund`
        )) || {};

      return response.data;
    } catch (ex) {
      console.error(ex);
      return null;
    }
  }
}
